<template>
  <actionable-panel
    :header-text="headerText" :complete="complete" :active="active"
    :current-step="currentStep" :total-steps="totalSteps" :disable-edit="editDisabled" @edit="transitionEdit"
  >
    <div class="wro-actionable-panel__body">
      <sb-dismissible-banner v-if="!isIPPMerchant && isSendDirectEnabled" class="wro-actionable-panel__welcome-hub">
        <strong>
          New! Merchants can customize regional hub and final inventory destination when sending inventory to ShipBob. Out-of-region final inventory destinations will incur an additional fee.
          <span v-if="!isPoweredBySubAccountUser">
            To learn more,
            <help-center-link link-key="createWro/nonIppSendDirectFees">
              click here.
            </help-center-link>
          </span>
        </strong>
      </sb-dismissible-banner>
      <sb-dismissible-banner v-else-if="hubFlowsEnabled && !isPoweredBySubAccountUser" class="wro-actionable-panel__welcome-hub">
        <strong>
          The US Southeast and Southwest Hub have no or limited auto-distribution. Please choose a different hub for auto-distribution to all regions when sending inventory to ShipBob. Learn more
          <help-center-link link-key="createWro/sendInventoryToHub">
            here.
          </help-center-link>
        </strong>
      </sb-dismissible-banner>
      <div class="div-alignment">
        <sb-field class="wro-actionable-panel__selection flex45">
          <sb-label>Sending To</sb-label>
          <sb-dropdown
            id="decide-where-shipping" ref="dropdownFcSelection" v-model="selectedFulfillmentCenter"
            :options="fulfillmentCenters" :loading-promise="loadingFcs" :disabled="disableCreate"
            @input="sendToChanged"
          >
            <sb-selected-option slot="selection" slot-scope="{ selected }">
              {{ selected.name }}
            </sb-selected-option>
            <sb-options>
              <sb-option slot-scope="{ option }" :value="option">
                {{ option.name }}
              </sb-option>
            </sb-options>
          </sb-dropdown>
          <sb-label v-if="!isWroCreated" class="wro-actionable-panel__tip">
            <b>Tip!</b> Don’t know where to send your inventory? Choose the warehouse closest to you or your customers.
          </sb-label>
          <sb-label v-else class="wro-actionable-panel__tip">
            Fulfillment centers can only be edited prior to shipment(s) being shipped. Fulfillment center selected must match
            what is on your shipping label.
          </sb-label>
        </sb-field>
        <div v-if="showAutoDistributeIfHubFc && isIPPMerchant && !isSendDirectEnabled" class="wro-actionable-panel__selection inventory-final flex55">
          <div v-if="isAutoDistribute" class="sb-form-field inventory-final-toggle">
            <sb-label> Auto Distribute </sb-label>
            <toggle ref="autoDistributeOffToggle" :checked.sync="toggleAutoDistribute" />
          </div>
          <div v-if="isAutoDistribute && spokeDestinationsForSelectedHub" class="sb-form-field inventory-final-destination">
            <sb-label class="text-spacing-inventory-distribution">
              Final Inventory Destination
            </sb-label>
            <sb-dropdown
              id="decide-where-shipping" ref="dropdownAutoDistributeOffFcselection"
              v-model="selectedDestinationFulfillmentCenter"
              :loading-promise="finalDestinationFcsWithLanesLoading"
              :options="spokeDestinationsForSelectedHub"
              :disabled="disableCreate || toggleAutoDistribute == true"
            >
              <sb-selected-option slot="selection" slot-scope="{ selected }">
                {{ selected.name }}
              </sb-selected-option>
              <sb-options>
                <sb-option slot-scope="{ option }" :value="option" class="final-dest-fc-option">
                  <sb-text :class="{'text-disabled': !option?.distributionAllowed}">
                    {{ option.name }}
                  </sb-text>
                  <div
                    v-if="!option?.distributionAllowed"
                    class="error-icon-wrapper"
                  >
                    <img src="@assets/images/error.svg" class="error-icon" alt="info">
                  </div>
                </sb-option>
              </sb-options>
            </sb-dropdown>
            <sb-text v-if="areThereUnavailableLanesForSelectedHub" class="unavailable-lanes-text">
              <img src="@assets/images/error.svg" class="error-icon" alt="info"> The destinations displayed with icon can not be selected...
            </sb-text>
          </div>
        </div>
        <sb-field v-if="showAutoDistributeIfHubFc && isSendDirectEnabled && availableSpokesForSelectedHub" class="wro-actionable-panel__selection flex55">
          <div class="inventory-destination" data-testid="hubSelectedDropdownDisplayed">
            <div class="flex50">
              <sb-label class="text-spacing-inventory-distribution">
                Final Inventory Destination
                <img src="@assets/images/i-icon.svg" alt="info" style="margin:0 0 3px 2px;">
                <sb-tooltip class="text-spacing-inventory-distribution__tooltip finalDestinationFcTooltip" orientation="bottom">
                  <p class="info__tooltip-title new-line-string-style" data-test-id="finalDestinationFcTooltipDisplayed">
                    <span v-if="isIPPMerchant">Select your inventory's destination.<br>
                      With “Auto-Distribute”, ShipBob intelligently allocates across regions. Use Auto unless you have specific needs like B2B shipments or temperature-sensitive stock.
                    </span>
                    <span v-else>Select your inventory's destination.<br>
                      With “Auto Select”, ShipBob will pick a fulfillment center within your selected hub region. Use Auto Select unless you have specific needs like B2B shipments or temperature-sensitive stock.<br>
                      Final destinations outside of your selected hub region will incur additional fees.<br>
                      <span v-if="!isPoweredBySubAccountUser">
                        To get a detailed breakdown of the per-unit pricing, <help-center-link link-key="createWro/nonIppSendDirectFees">click here</help-center-link>.
                      </span>
                    </span>
                  </p>
                </sb-tooltip>
              </sb-label>
            </div>
            <sb-dropdown
              id="decide-where-shipping" ref="dropdownAutoDistributeOffFcselection"
              v-model="selectedDestinationFulfillmentCenter" :options="usAccessibleSpokefulfillmentCenters"
              :loading-promise="finalDestinationFcsWithLanesLoading" :disabled="disableCreate || toggleAutoDistribute == true"
              data-testid="finalDestinationFcDropdown"
              @input="sendToDirectChanged"
            >
              <sb-selected-option slot="selection" slot-scope="{ selected }">
                {{ selected.name }}
              </sb-selected-option>
              <sb-options>
                <sb-option slot-scope="{ option }" :value="option" class="extra-fee">
                  <sb-text :class="{'text-disabled': !option?.distributionAllowed}">
                    {{ option.name }}
                  </sb-text>
                  <div
                    v-if="!option?.distributionAllowed"
                    class="error-icon-wrapper"
                  >
                    <img src="@assets/images/error.svg" class="error-icon" alt="info">
                    <sb-field-helper-text v-if="option?.distributionAllowed && shouldDisplayExtraFee(option)">
                      Extra fee required
                    </sb-field-helper-text>
                  </div>
                </sb-option>
              </sb-options>
            </sb-dropdown>
            <sb-text v-if="areThereUnavailableLanesForSelectedHub" class="unavailable-lanes-text">
              <img src="@assets/images/error.svg" class="error-icon" alt="info"> The destinations displayed with icon can not be selected...
            </sb-text>
            <sb-label v-if="selectedDestinationFulfillmentCenter?.distributionAllowed && shouldDisplayExtraFee(selectedDestinationFulfillmentCenter)" class="wro-actionable-panel__tip" data-testid="additionalFeeAccepted">
              Additional fee accepted for stowing outside hub region
            </sb-label>
          </div>
        </sb-field>
      </div>
      <div v-if="shouldShowAutoDistributeModal && isIPPMerchant && !isSendDirectEnabled" class="text-spacing-description" data-testid="ippAutoDistributeTextIfHub">
        ShipBob will Auto Distribute your inventory to improve shipping and storage costs and speed up delivery times to
        your customers.
        To turn off auto distribute,
        <sb-button
          variation="link" data-testid="openAutoDistributeOffModal"
          @click="showAutoDistribute"
        >
          click here
        </sb-button>.
      </div>
    </div>
    <expand-transition>
      <sb-banner v-if="serviceDisabled" variation="warning">
        This FC is currently not accepting inventory. Please select another FC to send inventory to or reach out to our
        support team for help.
      </sb-banner>
    </expand-transition>

    <sb-field class="wro-actionable-panel__field" :variation="errorVariation('purchaseOrderNumber')">
      <sb-label>PO # (Optional)</sb-label>
      <sb-text-input :maxlength="150" :value="purchaseOrderNumber" @input="(pon) => handlePurchaseOrderNumber(pon)" />
      <sb-field-helper-text>
        <p />
      </sb-field-helper-text>
    </sb-field>
    <sb-panel v-if="!hubFlowsEnabled && !isPoweredBySubAccountUser" slot="info" class="wro-actionable-panel__info-panel" type="sub">
      <h4 class="wro-actionable-panel__info-panel-title">
        Heads Up!
      </h4>
      <p v-if="showLocalCurrencyWithSymbolAndCode" class="wro-actionable-panel__info-panel-note">
        Received orders are securely unloaded and your items are stored in bar-coded locations. This is priced based on man-hours.
      </p>
      <p v-else class="wro-actionable-panel__info-panel-note">
        Each receiving order costs $25 to securely unload and store your items in bar-coded locations. For receiving orders that take longer than 2 hours, it will cost $40 per additional man-hour.
      </p>
      <p v-if="!isPoweredBySubAccountUser" class="wro-actionable-panel__info-panel-note">
        To learn more about Receiving Order costs,
        <help-center-link link-key="createWro/sendInventoryToFC">
          click here
        </help-center-link>.
      </p>
    </sb-panel>
    <div slot="footer" class="wro-actionable-panel__footer">
      <div v-if="anyFCChanged" class="wro-actionable-panel__footer__checkbox">
        <sb-checkbox v-model="isFCChangeChecked">
          <p>
            I confirm that my shipment(s) are not already in transit and the shipping labels match the fulfillment center
            selected above.
          </p>
        </sb-checkbox>
      </div>
      <div>
        <sb-async-button
          :loading-promise="savingWro" :disabled="shouldBeDisabled"
          class="wro-save-button wro-actionable-panel__action" @click="transitionSave"
        >
          Save & Continue
        </sb-async-button>
        <a class="wro-actionable-panel__action" href="#" @click.prevent="transitionClear">Cancel</a>
      </div>
    </div>
    <sb-modal
      :active="autoDistributeModalVisible" :escape-trigger="true" :click-outside-trigger="false"
      class="reduce-modal-size" @close="handleAutoDistributeModalClose"
    >
      <h1 data-testid="autoDistrbuteModalTitle">
        <b>Are you sure you want to turn off Auto Distribute for this WRO?</b>
      </h1>
      <p>
        Doing so may result in poorly distributed inventory and/or increased shipping costs, storage costs or delivery
        times. ShipBob is not liable for the impact due to turning off Auto Distribution. If you wish to continue, please
        select a fulfillment center or hub where you would like this WRO's inventory to route to after receiving:
      </p>
      <sb-dropdown
        id="decide-where-shipping" ref="fulfillmentCenterDropdownSelected"
        v-model="selectedDestinationFulfillmentCenter"
        :options="spokeDestinationsForSelectedHub"
        :loading-promise="finalDestinationFcsWithLanesLoading"
        :disabled="disableCreate"
      >
        <sb-selected-option slot="selection" slot-scope="{ selected }">
          {{ selected.name }}
        </sb-selected-option>
        <sb-options>
          <sb-option slot-scope="{ option }" :value="option" :disabled="!option?.distributionAllowed" class="final-dest-fc-option">
            <sb-text :class="{'text-disabled': !option?.distributionAllowed}">
              {{ option.name }}
            </sb-text>
            <div
              v-if="!option?.distributionAllowed"
              class="error-icon-wrapper"
            >
              <img src="@assets/images/error.svg" class="error-icon" alt="info">
            </div>
          </sb-option>
        </sb-options>
      </sb-dropdown>
      <sb-text v-if="areThereUnavailableLanesForSelectedHub" class="unavailable-lanes-text">
        <img src="@assets/images/error.svg" class="error-icon" alt="info"> The destinations displayed with icon can not be selected...
      </sb-text>
      <br>
      <br>
      <div class="setmiddlewithspacing">
        <sb-button
          variation="warning" :disabled="selectedDestinationFulfillmentCenter == null || !selectedDestinationFulfillmentCenter?.distributionAllowed"
          data-testid="autoDistributeOffButton" @click="handleAutoDistributeModalClose"
        >
          Turn Off Auto Distribute
        </sb-button>
      </div>
      <div class="setmiddle">
        <sb-button variation="tertiary" size="small" @click="handleAutoDistributeModalClose">
          Cancel
        </sb-button>
      </div>
    </sb-modal>
    <confirm-extra-fee-modal :show-modal="showExtraFeeModal" :hub-selected="selectedFulfillmentCenterName" @feeAccepted="feeAccepted" @feeRejected="feeRejected" />
  </actionable-panel>
</template>

<script>
import { validatePurchaseOrderNumber } from './helper'
import cloneDeep from 'lodash/cloneDeep'
import stepsMixin from './stepsMixin'
import ServiceTypes from '@/enums/modules/fulfillmentCenter/serviceType.js'
import ConfirmExtraFeeModal from './whereshipping/ConfirmExtraFeeModal'

export default {
  name: 'WhereShipping',
  components: { ConfirmExtraFeeModal },
  filters: {},
  mixins: [stepsMixin],
  inject: ['isPoweredBySubAccountUser'],
  props: {},
  data: () => ({
    errors: {},
    autoDistributeModalVisible: false,
    showExtraFeeModal: false,
    isCheckedFCChanged: false,
    isFCChanged: false,
    isDestinationFCChanged: false,
    isAutoDistribute: false
  }),
  computed: {
    headerText () {
      return `Which ${this.isPoweredBySubAccountUser ? '' : 'ShipBob '}warehouse are you shipping to?`
    },
    anyFCChanged () {
      return this.isWroCreated && (this.isFCChanged || this.isDestinationFCChanged)
    },
    availableSpokesForSelectedHub () {
      const destinations = this.$shipBobStore.getters['createWarehouseReceivingOrder/inventoryDistributionDestinationsForHub'] ?? []
      return destinations
        .filter(fc => fc?.distributionAllowed)
        .map(fc => fc.id)
    },
    shouldBeDisabled () {
      if (this.selectedDestinationFulfillmentCenter?.id && !this.selectedDestinationFulfillmentCenter?.distributionAllowed) {
        return true
      }
      if (this.isWroCreated && this.anyFCChanged) {
        return this.disableSave || !this.isFCChangeChecked
      } else {
        return this.disableSave
      }
    },
    defaultFulfillmentCenter () {
      return { id: null, name: this.isIPPMerchant ? 'Auto Distribute (Recommended)' : 'Auto Select (Recommended)' }
    },
    isIPPMerchant: {
      get () {
        return this.$shipBobStore.getters.enableInventoryPlacementProgram
      }
    },
    shouldRetrieveHubAssociatedSpokes () {
      return this.showAutoDistributeIfHubFc && this.isSendDirectEnabled
    },
    isFCChangeChecked: {
      get () {
        return this.isCheckedFCChanged
      },
      set (val) {
        this.isCheckedFCChanged = val
      }
    },
    toggleAutoDistribute: {
      get () {
        return this.$shipBobStore.getters[this.StoreModuleName + '/getAutoDistributeToggleState']
      },
      set (val) {
        this.$shipBobStore.commit(this.StoreModuleName + '/changeAutoDistributeToggle', val)
        if (val) {
          this.selectedDestinationFulfillmentCenter = null
        }
      }
    },
    serviceDisabled () {
      const receivingService = this.selectedFulfillmentCenter?.getService(ServiceTypes.Receiving)
      return receivingService && !receivingService.enabled
    },
    fulfillmentCenters () {
      return this.$shipBobStore.getters['fulfillmentCenter/createWro']
    },
    spokeFulfillmentCenterIds () {
      return this.spokeDestinationsForSelectedHub.map(fc => fc.id)
    },
    hubFulfillmentCenterIds () {
      const allUserFulfillmentCenters = this.$shipBobStore.getters['fulfillmentCenter/fulfillmentCentersWithType']
      // fc type 5 will be hubs
      return allUserFulfillmentCenters.filter(fc => fc.type === 5).map(fc => fc.id)
    },
    usAccessibleSpokefulfillmentCenters () {
      return [
        { ...this.defaultFulfillmentCenter, distributionAllowed: true },
        ...this.spokeDestinationsForSelectedHub ?? []
      ]
    },
    shouldShowAutoDistributeModal () {
      return this.hubFulfillmentCenterIds.includes(this.selectedFulfillmentCenter?.id) && !this.isAutoDistribute
    },
    user () {
      return this.$shipBobStore.getters['user/getUser']
    },
    isSendDirectEnabled () {
      const setting = this.$shipBobStore.getters['createWarehouseReceivingOrder/getSendDirectEnabled']
      if (setting) {
        if (setting.Value === '*') return true
        const users = setting.Value.split(',').map(x => x.trim())
        const userId = this.user.impersonatedUserId ?? this.user.id
        return users.includes(userId.toString())
      }
      return false
    },
    showAutoDistributeIfHubFc () {
      return this.selectedFulfillmentCenter && this.hubFulfillmentCenterIds.includes(this.selectedFulfillmentCenter.id)
    },
    selectedDestinationFulfillmentCenter: {
      get () {
        return this.$shipBobStore.getters[this.StoreModuleName + '/destinationFulfillmentCenter']
      },
      set (fc) {
        this.$shipBobStore.dispatch(this.StoreModuleName + '/setDestinationFc', fc)
        const draftFCId = this.draft?.destinationFulfillmentCenter?.id ?? -1 // treat undifined or id null as equal
        const wroFCId = this.editWro?.destinationFulfillmentCenter?.id ?? -1 // treat undifined or id null as equal
        if (this.isWroCreated && draftFCId !== wroFCId) {
          this.isDestinationFCChanged = true
        } else {
          this.isDestinationFCChanged = false
        }
        this.draft.hasFCChanged = this.anyFCChanged
        this.$shipBobStore.dispatch(this.StoreModuleName + '/setFlagForChange', { flag: 'fcChange', value: this.anyFCChanged })
        this.transitionInput()
      }
    },
    selectedFulfillmentCenter: {
      get () {
        return this.$shipBobStore.getters[this.StoreModuleName + '/fulfillmentCenter']
      },
      set (fc) {
        this.$shipBobStore.dispatch(this.StoreModuleName + '/setFc', fc)
        const draftFCId = this.draft?.fulfillmentCenter?.id ?? -1 // treat undifined or id null as equal
        const wroFCId = this.editWro?.fulfillmentCenter?.id ?? -1 // treat undifined or id null as equal
        if (draftFCId !== wroFCId) {
          this.isFCChanged = true
        } else {
          this.isFCChanged = false
        }
        this.draft.hasFCChanged = this.anyFCChanged
        this.$shipBobStore.dispatch(this.StoreModuleName + '/setFlagForChange', { flag: 'fcChange', value: this.anyFCChanged })
        if (this.shouldRetrieveHubAssociatedSpokes) {
          this.$shipBobStore.dispatch(this.StoreModuleName + '/getSpokesForHubId', fc.id)
        }
        this.transitionInput()
      }
    },
    selectedFulfillmentCenterName () {
      return this.selectedFulfillmentCenter?.name
    },
    purchaseOrderNumber () {
      return this.$shipBobStore.getters[this.StoreModuleName + '/purchaseOrderNumber']
    },
    loadingFcs () {
      return this.$shipBobStore.getters.loading('getReceivingFulfillmentCenters')
    },
    disableCreate () {
      return this.$shipBobStore.getters['warehouseReceivingOrders/hasReachedDraftLimit'] && this.draftId === undefined
    },
    draftId () {
      return this.draft.id
    },
    showLocalCurrencyWithSymbolAndCode () {
      return this.$shipBobStore.getters.showLocalCurrencyWithSymbolAndCode
    },
    hubFlowsEnabled () {
      return this.$shipBobStore.getters['fulfillmentCenter/hubFlowsEnabled']
    },
    spokeDestinationsForSelectedHub () {
      return this.$shipBobStore.getters['createWarehouseReceivingOrder/inventoryDistributionDestinationsForHub'] ?? []
    },
    finalDestinationFcsWithLanesLoading () {
      return this.$shipBobStore.getters.loading('destinationsLoading')
    },
    areThereUnavailableLanesForSelectedHub () {
      return this.spokeDestinationsForSelectedHub.some(x => !x.distributionAllowed)
    }
  },
  watch: {
    selectedFulfillmentCenter (n, o) {
      if (this.isWroCreated && o && (this.draft.fulfillmentCenter?.id !== this.editWro.fulfillmentCenter.id)) this.isFCChanged = true

      if (o === null && n && n.id) {
        const matchedFc = this.fulfillmentCenters.find(fc => fc.id === n.id)

        if (matchedFc) {
          this.selectedFulfillmentCenter = matchedFc
        }
      }
      if (this.hubFulfillmentCenterIds.includes(n.id)) {
        this.$shipBobStore.dispatch('createWarehouseReceivingOrder/getInventoryDistributionDestinations', n.id)
      }
      if (!this.selectedDestinationFulfillmentCenter && this.isSendDirectEnabled) {
        this.selectedDestinationFulfillmentCenter = this.defaultFulfillmentCenter
      }
    },
    selectedDestinationFulfillmentCenter (n, o) {
      if (this.isWroCreated && o && this.draft.destinationFulfillmentCenter && this.editWro.destinationFulfillmentCenter && (this.draft.destinationFulfillmentCenter.id !== this.editWro.destinationFulfillmentCenter.id)) this.isDestinationFCChanged = true
      if ((o === null || o.id === null) && n && n.id) {
        const matchedFc = this.usAccessibleSpokefulfillmentCenters.find(fc => fc.id === n.id)
        if (matchedFc) {
          this.autoDistributeOff()
          this.toggleAutoDistribute = false
          this.selectedDestinationFulfillmentCenter = matchedFc
        }
      }
    }
  },
  async mounted () {
    this.transitionClear()
  },
  async created () {
    await this.$shipBobStore.dispatch('createWarehouseReceivingOrder/getSendDirectSetting', { loadingScopes: ['setupWro'] })
    await this.$shipBobStore.dispatch('fulfillmentCenter/getFulfillmentCenters', { loadingScopes: ['setupWro'] })
    await this.$shipBobStore.dispatch('fulfillmentCenter/getFulfillmentCentersWithType', { loadingScopes: ['setupWro'] })
  },
  methods: {
    sendToChanged () {
      // reset destination every time the sending to changes
      this.selectedDestinationFulfillmentCenter = this.isSendDirectEnabled ? this.defaultFulfillmentCenter : null
    },
    sendToDirectChanged (selected) {
      if (this.showAutoDistributeIfHubFc && this.isSendDirectEnabled && this.shouldDisplayExtraFee(selected) && selected?.distributionAllowed) {
        this.openExtraFeeModal()
      }
    },
    errorVariation (type) {
      return this.errors[type] ? 'error' : 'default'
    },
    handleAutoDistributeModalClose () {
      this.autoDistributeModalVisible = false
    },
    showAutoDistribute () {
      if (!this.spokeDestinationsForSelectedHub) {
        this.$shipBobStore.dispatch('createWarehouseReceivingOrder/getInventoryDistributionDestinations', this.selectedFulfillmentCenter.id)
      }
      this.autoDistributeModalVisible = true
    },
    autoDistributeOff () {
      this.isAutoDistribute = true
    },
    isfcLocatedInUS (fc) {
      return fc.services.some(s => s.address.country === 'US')
    },
    setErrors (type, valid) {
      const errors = cloneDeep(this.errors)
      const key = type
      if (valid) {
        delete errors[key]
      } else {
        errors[key] = true
      }
      this.errors = errors
    },
    handlePurchaseOrderNumber (pon) {
      const valid = validatePurchaseOrderNumber(pon)
      this.setErrors('purchaseOrderNumber', valid)
      this.$shipBobStore.dispatch(this.StoreModuleName + '/setPurchaseOrderNumber', pon)
      this.draft.hasPurchaseOrderNumberChanged = this.isWroCreated && this.draft.purchaseOrderNumber !== this.editWro.purchaseOrderNumber
      this.transitionInput()
    },
    shouldDisplayExtraFee (fulfillmentOption) {
      if (this.isIPPMerchant) return false
      return fulfillmentOption?.id && !this.availableSpokesForSelectedHub?.includes(fulfillmentOption?.id)
    },
    openExtraFeeModal () {
      this.showExtraFeeModal = true
    },
    feeAccepted () {
      this.showExtraFeeModal = false
    },
    feeRejected () {
      this.showExtraFeeModal = false
      this.selectedDestinationFulfillmentCenter = this.defaultFulfillmentCenter
    }
  },
  i18n: { messages: {} }
}
</script>

<style scoped lang='scss'>
@import './panel.scss';

.div-alignment {
  display: flex;
  flex-wrap: nowrap;
  justify-content: space-between;
}

@media screen and (max-width: 768px) {
  .product-overview-input {
    display: flex !important;
    flex-direction: column;
  }
}

.wro-actionable-panel__footer__checkbox {
  display: flex;
}

.new-line-string-style {
  white-space: pre-line;
}

.wro-actionable-panel__field {
  margin-top: 1rem;
}

.wro-actionable-panel__tip {
  margin-top: 0.5em;
}

.setmiddle {
  text-align: center;
  width: 100%;
}

.setmiddlewithspacing {
  text-align: center;
  width: 100%;
  height: 45px;
  margin-bottom: 15px;
}

.unavailable-lanes-text {
  font-size: 0.875rem;
  margin-top: 0.5em;
  font-weight: 600;
  color: #737883;
}

.text-spacing-inventory-distribution {
  display: block;
  &__tooltip {
    padding: 1em;
    max-width: 20em;
    display: flex;
  }
}

.text-spacing-description {
  margin-top: 1em;
}

.reduce-modal-size {
  padding-left: 20%;
  padding-right: 20%;
}

.flex50 {
  flex-basis: 50%;
}

.flex55 {
  flex-basis: 55%;
}

.flex45 {
  flex-basis: 45%;
}

.inventory-destination {
  display: inline-block;
  margin-left: 1.5em;
}

.final-dest-fc-option {
  display: flex !important;
  gap: 5px;
}

.inventory-final {
  display: flex;
  flex-wrap: nowrap;
  justify-content: space-between;
}

.inventory-final-destination {
  flex: 1;
  margin-left: 0.75em;
}

.inventory-final-toggle {
  text-align: center;
  align-items: center;
  margin-left: 0.75em;
}

.extra-fee {
  display: flex!important;
  justify-content: space-between;
}

.extra-fee > p {
  margin-right: 0.75em!important;
  margin-left: 0.75em!important;
}

.text-disabled {
 color: grey
}

.error-icon-wrapper p {
 margin: var(--space-xs);
 display: inline-block;
}

.error-icon {
 color: grey;
 margin-left: 6px;
 display: inline-block;
 font-size: 0.75rem;
 text-decoration: none;
 width: 1.0rem;
 height: 1.0rem;
 margin-top: -3px;
}

</style>
